import { useTranslation } from "react-i18next";
import PwaInstallButton from "../../shared/components/PwaInstallButton";
import LanguageSwitcher from "../../shared/components/LanguageSwitcher";
import UpdatesTerminal from "../components/UpdatesTerminal";

const LAUNCH_CARD_IDS = ["songRequests", "publicPage", "gigTools"];

const PROOF_ITEM_IDS = [
  "noAudienceApp",
  "noPaperNotes",
  "noLostMessages",
  "noMessySocialComments",
];

const AUDIENCE_TYPE_IDS = [
  "soloArtists",
  "bands",
  "duos",
  "djs",
  "karaokeNights",
  "restaurantGigs",
  "shipGigs",
  "socialLives",
];

function LaunchCard({ title, text }) {
  return (
    <article className="group rounded-[1.75rem] border border-white/10 bg-white/[0.035] p-6 shadow-2xl shadow-black/20 transition duration-300 hover:-translate-y-1 hover:border-red-400/30 hover:bg-red-500/[0.055]">
      <h3 className="text-2xl font-black uppercase leading-none tracking-[-0.055em] text-white">
        {title}
      </h3>

      <p className="mt-4 text-sm font-semibold leading-7 text-zinc-400">
        {text}
      </p>
    </article>
  );
}

export default function WaitlistPage() {
  const { t, i18n } = useTranslation("admin");

  const updateLanguage = i18n.resolvedLanguage?.toLowerCase().startsWith("en")
    ? "en"
    : "fi";

  const launchCards = LAUNCH_CARD_IDS.map((id) => ({
    id,
    title: t(`waitlistPage.launchCards.${id}.title`),
    text: t(`waitlistPage.launchCards.${id}.text`),
  }));

  const proofItems = PROOF_ITEM_IDS.map((id) => ({
    id,
    label: t(`waitlistPage.proofItems.${id}`),
  }));

  const audienceTypes = AUDIENCE_TYPE_IDS.map((id) => ({
    id,
    label: t(`waitlistPage.audienceTypes.${id}`),
  }));

  return (
    <main className="min-h-screen overflow-hidden bg-[#040404] text-white">
      <style>
        {`
          @keyframes paranoidiLaunchReveal {
            0% {
              opacity: 0;
              transform: translateY(26px) scale(0.985);
              filter: blur(12px);
            }

            100% {
              opacity: 1;
              transform: translateY(0) scale(1);
              filter: blur(0);
            }
          }

          @keyframes paranoidiLogoHit {
            0% {
              opacity: 0;
              letter-spacing: -0.16em;
              transform: scale(0.96);
              filter: blur(14px);
            }

            72% {
              opacity: 1;
              letter-spacing: -0.085em;
              transform: scale(1.015);
              filter: blur(0);
            }

            100% {
              opacity: 1;
              letter-spacing: -0.095em;
              transform: scale(1);
              filter: blur(0);
            }
          }

          @keyframes paranoidiRedSweep {
            0% {
              transform: translateX(-120%) skewX(-18deg);
              opacity: 0;
            }

            18% {
              opacity: 0.75;
            }

            100% {
              transform: translateX(140%) skewX(-18deg);
              opacity: 0;
            }
          }

          @keyframes paranoidiGlowBreathe {
            0%, 100% {
              opacity: 0.28;
              transform: scale(1);
            }

            50% {
              opacity: 0.62;
              transform: scale(1.08);
            }
          }

          @keyframes paranoidiSlowDrift {
            0%, 100% {
              transform: translate3d(0, 0, 0);
            }

            50% {
              transform: translate3d(0, -10px, 0);
            }
          }

          .paranoidi-launch-reveal {
            animation: paranoidiLaunchReveal 780ms
              cubic-bezier(0.16, 1, 0.3, 1) both;
          }

          .paranoidi-delay-1 {
            animation-delay: 120ms;
          }

          .paranoidi-delay-2 {
            animation-delay: 220ms;
          }

          .paranoidi-delay-3 {
            animation-delay: 340ms;
          }

          @keyframes paranoidiLanguageReveal {
            0% {
              opacity: 0;
              transform: translateY(-8px) scale(0.96);
              filter: blur(6px);
            }

            100% {
              opacity: 1;
              transform: translateY(0) scale(1);
              filter: blur(0);
            }
          }

          .paranoidi-hero-language-wrap {
            animation: paranoidiLanguageReveal 620ms
              cubic-bezier(0.16, 1, 0.3, 1) 260ms both;
          }

          .paranoidi-hero-language {
            gap: 0 !important;
          }

          .paranoidi-hero-language > span:first-child {
            display: none;
          }
        `}
      </style>

      <section className="relative flex min-h-screen items-center overflow-hidden border-b border-white/10 px-5 py-10 sm:px-8 lg:px-10">
        <div className="absolute inset-0 overflow-hidden">
          <video
            className="pointer-events-none h-full w-full scale-110 object-cover object-center opacity-[0.24]"
            src="/videos/paranoidi-hero-guitar-720.mp4"
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            aria-hidden="true"
            onCanPlay={(event) => {
              event.currentTarget.play().catch(() => {});
            }}
          />

          <div className="absolute inset-0 bg-black/68" />

          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_22%,rgba(239,68,68,0.34),transparent_30rem),radial-gradient(circle_at_18%_74%,rgba(255,255,255,0.08),transparent_26rem),linear-gradient(135deg,rgba(127,29,29,0.26),transparent_52%)]" />

          <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.022)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.022)_1px,transparent_1px)] bg-[size:46px_46px]" />

          <div className="absolute left-1/2 top-[18%] h-[28rem] w-[28rem] -translate-x-1/2 rounded-full bg-red-500/12 blur-3xl [animation:paranoidiGlowBreathe_5.5s_ease-in-out_infinite]" />

          <div className="absolute left-0 top-[38%] h-20 w-1/2 bg-gradient-to-r from-transparent via-red-400/20 to-transparent blur-xl [animation:paranoidiRedSweep_4.8s_ease-in-out_1.2s_infinite]" />
        </div>

        <div className="theme-dark paranoidi-hero-language-wrap absolute right-4 top-4 z-30 sm:right-6 sm:top-6 lg:right-10 lg:top-8">
          <LanguageSwitcher
            variant="public"
            className="paranoidi-hero-language !mt-0"
          />
        </div>

        <div className="relative mx-auto grid w-full max-w-[90rem] gap-10 xl:grid-cols-[1fr_0.72fr] xl:items-center">
          <div className="text-center lg:text-left">
            <h1 className="text-[3.3rem] font-black uppercase leading-[0.78] text-white sm:text-7xl md:text-8xl lg:text-[8.5rem]">
              <span className="block [animation:paranoidiLogoHit_900ms_cubic-bezier(0.16,1,0.3,1)_both]">
                Paranoidi
              </span>
            </h1>

            <div className="paranoidi-launch-reveal paranoidi-delay-1">
              <p className="mx-auto mt-7 max-w-3xl text-2xl font-black uppercase leading-[1.05] tracking-[-0.055em] text-white sm:text-4xl lg:mx-0">
                {t("waitlistPage.hero.headlineLine1")}
                <br />
                {t("waitlistPage.hero.headlineLine2")}
              </p>

              <p className="mx-auto mt-6 max-w-2xl text-base font-semibold leading-8 text-zinc-300 sm:text-lg lg:mx-0">
                {t("waitlistPage.hero.intro")}
              </p>
            </div>

            <div className="paranoidi-launch-reveal paranoidi-delay-2 mt-8 flex flex-col justify-center gap-3 sm:flex-row sm:flex-wrap lg:justify-start">
              <a
                href="/login"
                className="inline-flex items-center justify-center rounded-full bg-red-500 px-7 py-4 text-sm font-black uppercase tracking-[0.14em] text-white shadow-2xl shadow-red-950/40 transition hover:-translate-y-0.5 hover:bg-red-400"
              >
                {t("waitlistPage.actions.startFree")}
              </a>

              <a
                href="/login"
                className="inline-flex items-center justify-center rounded-full border border-white/25 bg-white/[0.08] px-7 py-4 text-sm font-black uppercase tracking-[0.14em] text-white transition hover:-translate-y-0.5 hover:border-white/40 hover:bg-white/[0.12]"
              >
                {t("waitlistPage.actions.login")}
              </a>

              <a
                href={
                  updateLanguage === "en"
                    ? "/en/features"
                    : "/ominaisuudet"
                }
                className="inline-flex items-center justify-center rounded-full border border-white/15 bg-white/[0.04] px-7 py-4 text-sm font-black uppercase tracking-[0.14em] text-white transition hover:-translate-y-0.5 hover:bg-white/[0.08]"
              >
                {t("waitlistPage.actions.features")}
              </a>
            </div>
          </div>

          <aside className="paranoidi-launch-reveal paranoidi-delay-2 relative mx-auto hidden w-full max-w-xl xl:block xl:max-w-none">
            <div className="absolute -inset-6 rounded-[3rem] bg-red-500/10 blur-3xl" />

            <div className="relative overflow-hidden rounded-[2rem] border border-white/10 bg-zinc-950/82 p-5 shadow-2xl shadow-black/50 backdrop-blur sm:p-6">
              <div className="rounded-[1.5rem] border border-white/10 bg-black/55 p-5">
                <p className="text-xs font-black uppercase tracking-[0.22em] text-red-400">
                  {t("waitlistPage.duringGig.eyebrow")}
                </p>

                <div className="mt-5 grid gap-3">
                  {proofItems.map((item, index) => (
                    <div
                      key={item.id}
                      className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.035] px-4 py-3"
                      style={{
                        animation: `paranoidiSlowDrift 5.2s ease-in-out ${
                          index * 280
                        }ms infinite`,
                      }}
                    >
                      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-red-500 text-xs font-black text-white">
                        ✓
                      </span>

                      <p className="text-sm font-black uppercase tracking-[0.04em] text-white">
                        {item.label}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                <div className="rounded-2xl border border-white/10 bg-red-500/[0.07] p-4">
                  <p className="text-xs font-black uppercase tracking-[0.18em] text-red-200">
                    {t("waitlistPage.duringGig.audience")}
                  </p>

                  <p className="mt-2 text-xl font-black tracking-[-0.05em] text-white">
                    {t("waitlistPage.duringGig.audienceAction")}
                  </p>
                </div>

                <div className="rounded-2xl border border-white/10 bg-white/[0.035] p-4">
                  <p className="text-xs font-black uppercase tracking-[0.18em] text-zinc-500">
                    {t("waitlistPage.duringGig.performer")}
                  </p>

                  <p className="mt-2 text-xl font-black tracking-[-0.05em] text-white">
                    {t("waitlistPage.duringGig.performerAction")}
                  </p>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </section>

      <UpdatesTerminal language={updateLanguage} />

      <section className="border-b border-white/10 bg-[#070707] px-5 py-14 sm:px-8 lg:px-10">
        <div className="mx-auto max-w-7xl">
          <p className="text-center text-xs font-black uppercase tracking-[0.24em] text-red-400">
            {t("waitlistPage.audience.eyebrow")}
          </p>

          <div className="mt-7 flex flex-wrap justify-center gap-3">
            {audienceTypes.map((item) => (
              <span
                key={item.id}
                className="rounded-full border border-white/10 bg-white/[0.035] px-5 py-3 text-xs font-black uppercase tracking-[0.12em] text-zinc-200"
              >
                {item.label}
              </span>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-16 sm:px-8 lg:px-10 lg:py-20">
        <div className="max-w-3xl">
          <p className="text-sm font-black uppercase tracking-[0.24em] text-red-400">
            {t("waitlistPage.about.eyebrow")}
          </p>

          <h2 className="mt-4 text-4xl font-black uppercase tracking-[-0.06em] text-white sm:text-5xl">
            {t("waitlistPage.about.title")}
          </h2>

          <p className="mt-5 text-base font-semibold leading-8 text-zinc-400">
            {t("waitlistPage.about.text")}
          </p>

          <div className="mt-7 rounded-[1.5rem] border border-white/10 bg-white/[0.035] p-5">
            <p className="text-sm font-black uppercase tracking-[0.16em] text-white">
              {t("waitlistPage.about.noAudienceAppTitle")}
            </p>

            <p className="mt-2 max-w-2xl text-sm font-semibold leading-7 text-zinc-400">
              {t("waitlistPage.about.noAudienceAppText")}
            </p>

            <PwaInstallButton
              className="mt-5 inline-flex items-center justify-center rounded-full border border-red-400/30 bg-red-500/10 px-6 py-3 text-xs font-black uppercase tracking-[0.14em] text-red-100 transition hover:-translate-y-0.5 hover:bg-red-500/20"
            >
              {t("waitlistPage.actions.install")}
            </PwaInstallButton>
          </div>
        </div>

        <div className="mt-10 grid gap-5 lg:grid-cols-3">
          {launchCards.map((card) => (
            <LaunchCard
              key={card.id}
              title={card.title}
              text={card.text}
            />
          ))}
        </div>
      </section>

      <section className="relative overflow-hidden border-y border-white/10 bg-[#120505]">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(239,68,68,0.24),transparent_32rem)]" />

        <div className="relative mx-auto grid max-w-7xl gap-8 px-5 py-16 sm:px-8 lg:grid-cols-[1fr_0.85fr] lg:items-center lg:px-10 lg:py-20">
          <div>
            <p className="text-sm font-black uppercase tracking-[0.24em] text-red-300">
              {t("waitlistPage.cta.eyebrow")}
            </p>

            <h2 className="mt-4 text-4xl font-black uppercase tracking-[-0.06em] text-white sm:text-5xl">
              {t("waitlistPage.cta.title")}
            </h2>
          </div>

          <div className="rounded-[2rem] border border-white/10 bg-black/45 p-6 shadow-2xl shadow-black/30 backdrop-blur">
            <p className="text-base font-semibold leading-8 text-red-50/75">
              {t("waitlistPage.cta.text")}
            </p>

            <div className="mt-6 grid gap-3">
              <a
                href="/login"
                className="inline-flex items-center justify-center rounded-full bg-red-500 px-6 py-4 text-sm font-black uppercase tracking-[0.14em] text-white transition hover:bg-red-400"
              >
                {t("waitlistPage.actions.startFree")}
              </a>

              <a
                href={
                  updateLanguage === "en"
                    ? "/en/song-requests"
                    : "/biisitoiveet-keikalle"
                }
                className="inline-flex items-center justify-center rounded-full border border-white/15 bg-white/[0.04] px-6 py-4 text-sm font-black uppercase tracking-[0.14em] text-white transition hover:bg-white/[0.08]"
              >
                {t("waitlistPage.actions.howRequestsWork")}
              </a>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}